import {
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewEncapsulation,
    input,
    viewChild,
    signal,
    ChangeDetectionStrategy,
} from "@angular/core"
import {CM_LOGGER_JOBS} from "@app/common/services/logging"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {GetExecutionPlanGQL} from "@app/platform/components/jobs/execution-plan/execution-plan.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {TippyService} from "@ngneat/helipopper"
import {TippyInstance} from "@ngneat/helipopper/config"
import {ExecutionPlanTooltipComponent} from "@platform/components/jobs/execution-plan-tooltip/execution-plan-tooltip.component"
import {drawGraph} from "@platform/components/jobs/execution-plan/graph"
import {ExecutionPlanNodeType} from "@platform/components/jobs/execution-plan/graph/types"
import {combineLatest, exhaustMap, Observable, Subscription} from "rxjs"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-execution-plan",
    templateUrl: "execution-plan.component.html",
    styleUrls: ["execution-plan.component.sass"],
    encapsulation: ViewEncapsulation.None,
    imports: [ExecutionPlanTooltipComponent],
})
export class ExecutionPlanComponent implements OnDestroy, OnInit {
    private readonly logger = inject(CM_LOGGER_JOBS)
    // the job's UUID
    readonly jobId$ = input.required<Observable<string | null | undefined>>()

    // the SVG element that contains the execution plan chart
    readonly $executionPlanSvg = viewChild<ElementRef<SVGElement>>("executionPlanSvg")

    readonly $executionPlanTooltip = viewChild.required("executionPlanTooltip", {read: TemplateRef})

    readonly $tooltipData = signal<ExecutionPlanNodeType | undefined>(undefined)

    private jobSubscription?: Subscription
    private tooltipInstances: TippyInstance[] = []

    private readonly destroyRef = inject(DestroyRef)
    private readonly getExecutionPlan = inject(GetExecutionPlanGQL)
    private readonly tippyService = inject(TippyService)

    readonly $showPlaceholder = signal(true)

    ngOnDestroy(): void {
        this.jobSubscription?.unsubscribe()
        this.tooltipInstances.forEach((tooltipInstance) => tooltipInstance.destroy())
    }

    ngOnInit() {
        combineLatest([
            this.jobId$().pipe(
                exhaustMap(async (id) => fetchThrowingErrors(this.getExecutionPlan)({id})),
                takeUntilDestroyed(this.destroyRef),
            ),
            import("d3"),
            import("d3-dag"),
        ]).subscribe(([{job}, d3, d3Dag]) => {
            this.$showPlaceholder.set(false)
            try {
                const nodes: ExecutionPlanNodeType[] = job?.executionPlan?.nodes ?? []
                drawGraph(
                    nodes,
                    {
                        create: (node: SVGGElement, data: ExecutionPlanNodeType) => {
                            this.tippyService
                                .create(node as unknown as HTMLElement, this.$executionPlanTooltip(), {
                                    theme: "light",
                                    animation: "scale-subtle",
                                    onShow: (instance: TippyInstance) => {
                                        this.$tooltipData.set(instance.data)
                                    },
                                })
                                .subscribe((instance) => {
                                    instance.data = data
                                    this.tooltipInstances.push(instance)
                                })
                        },
                        update: (node: SVGGElement, data: ExecutionPlanNodeType) => {
                            const tooltipInstance = this.tooltipInstances.find((tooltipInstance) => {
                                return data.id === tooltipInstance.data.id
                            })
                            if (tooltipInstance) {
                                tooltipInstance.data = data

                                if (tooltipInstance.state.isVisible) {
                                    this.$tooltipData.set(data)
                                }
                            } else {
                                this.logger.error("Could not find tooltip instance to update", data.id)
                            }
                        },
                        remove: (node: SVGGElement) => {
                            const tooltipId = node.getAttribute("data-tooltip-id")
                            const existingTooltipInstance = this.tooltipInstances.find((tooltipInstance) => {
                                return tooltipId === tooltipInstance.data.id
                            })
                            if (existingTooltipInstance) {
                                this.tooltipInstances = this.tooltipInstances.filter((tooltipInstance) => {
                                    return existingTooltipInstance.id !== tooltipInstance.id
                                })
                                if (this.$tooltipData()?.id === existingTooltipInstance.data.id) {
                                    this.$tooltipData.set(undefined)
                                }
                                existingTooltipInstance.destroy()
                            } else {
                                this.logger.error("Could not find tooltip instance to remove", node)
                            }
                        },
                    },
                    d3,
                    d3Dag,
                )
            } catch (e) {
                this.logger.error("Error drawing execution plan graph:", e)
            }
        })
    }
}
